import React, { ReactElement, useEffect, useState } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

import { useVisibleState } from '@foxino/components-common'
import { Button, Loader, TextInput } from '@foxino/components-web'

import { AlertDialog } from '@app/components'
import { UserRole } from '@app/data'
import { useTranslation } from '@app/locales'
import { useUserProfile } from '@app/modules/settings/data/useUserProfile'
import { useUserRole } from '@app/modules/settings/data/useUserRole'
import { showToast } from '@app/utils/showToast'

import { authStore } from '../store/authStore'
import { IncorrectRole } from './IncorrectRole'

// TODO: remove registration code after beta testing
const REGISTRATION_CODE = 'ms2024'

type Props = {
  children: React.ReactNode | React.ReactNode[] | null
}

export const RoleProvider = ({ children }: Props): ReactElement | null => {
  const { t } = useTranslation(['error', 'common'])

  const { roles } = authStore.useStore((store) => store.userInfo)

  const { addUserRole, pending } = useUserRole()
  const { keycloak } = useKeycloak()
  const [isDialogOpen, openDialog, hideDialog] = useVisibleState()
  const [registrationCode, setDeleteConfirmationText] = useState<string>('')

  const { data } = useUserProfile()

  useEffect(() => {
    const addRole = async () => {
      const success = await addUserRole(UserRole.TEACHER)

      if (success?.data?.addUserRole) {
        await keycloak.updateToken(3600)
      }
    }

    if (
      roles &&
      !roles.includes(UserRole.TEACHER.toLocaleLowerCase()) &&
      !roles.includes(UserRole.STUDENT.toLocaleLowerCase())
    ) {
      addRole()
    }
  }, [roles?.length])

  useEffect(() => {
    if (data?.id) {
      posthog.identify(data.id, {
        $email: data.email
      })
      Sentry.setUser({ id: data.id })
    }
  }, [data?.id])

  const addTeacherRoleToStudent = async () => {
    if (registrationCode === REGISTRATION_CODE) {
      const success = await addUserRole(UserRole.TEACHER)

      if (success?.data?.addUserRole) {
        await keycloak.updateToken(3600)
      }
    } else {
      hideDialog()
      showToast(t('registrationCode.error'), { type: 'error' })
    }
  }

  if (pending) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader />
      </div>
    )
  }

  if (roles && roles.length > 0 && !roles.includes(UserRole.TEACHER.toLocaleLowerCase())) {
    return (
      <main className="flex h-screen w-full items-center justify-center">
        <AlertDialog
          className="select-none"
          title={t('registrationCode.title')}
          description={t('registrationCode.description')}
          isOpen={isDialogOpen}
          onAbort={hideDialog}
          actionButtons={
            <div className="flex w-full flex-col justify-center gap-10">
              <TextInput
                onPasteCapture={(e) => {
                  e.preventDefault()
                }}
                placeholder={t('registrationCode.placeholder')}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                maxLength={20}
              />

              <div className="flex w-full items-center gap-2">
                <Button className="w-full" color="secondary" onClick={hideDialog}>
                  {t('cancel')}
                </Button>
                <Button
                  className="w-full text-white"
                  disabled={registrationCode === ''}
                  onClick={addTeacherRoleToStudent}
                >
                  {t('submit')}
                </Button>
              </div>
            </div>
          }
        />
        <IncorrectRole onContinueClick={openDialog} />
      </main>
    )
  }

  return <>{children}</>
}
